// ============================================
// Field Alias Map
// Maps Business Architect variable IDs to
// current BrandConfig field paths.
//
// The Variable Registry uses descriptive IDs;
// BrandConfig uses existing field names.
// This map is the bridge — NO BrandConfig
// schema changes needed.
// ============================================

// ── FORWARD MAP: Variable Registry ID → BrandConfig field path ──

export const FIELD_ALIAS_MAP: Record<string, string> = {
  // ── Round 0 (BA) / Round 1 (Gen) — Identity & Formation ──
  'legalEntityName': 'brandEntity',
  'brandName': 'brandName',
  'brandDomain': 'brandDomain',
  'stateOfFormation': 'stateOfFormation',
  'ein': 'ein',
  'marketVertical': 'marketVertical',
  'productType': 'productType',
  'effectiveDate': 'effectiveDate',
  'businessAddress': 'businessAddress',
  'registeredAgentAddress': 'registeredAgentAddress',

  // ── Round 0 (BA) / Round 1 (Gen) — Leadership ──
  'ceoName': 'ceoName',
  'ceoEmail': 'ceoEmail',
  'trustName': 'trustName',
  'trustTrusteeName': 'trustTrusteeName',

  // ── Round 1 (BA) / Round 2 (Gen) — People & Clinical ──
  'medicalDirectorName': 'medicalDirectorName',
  'medicalDirectorCredentials': 'mdCredentials',
  'medicalDirectorLicenseStates': 'mdLicenseStates',
  'pcEntityName': 'pcEntityName',
  'pcOwnerName': 'pcOwnerName',

  // ── Round 1 (BA) / Round 2 (Gen) — Contacts ──
  'legalEmail': 'legalEmail',
  'privacyEmail': 'privacyEmail',
  'supportEmail': 'supportEmail',
  'complianceEmail': 'complianceEmail',
  'dmcaEmail': 'dmcaEmail',

  // ── Round 2 (BA) / Round 3 (Gen) — Partners & Vendors ──
  'telehealthNetworkName': 'telehealthNetwork',
  'pharmacyPartnerName': 'pharmacyPartner',
  'paymentProcessorName': 'paymentProcessor',
  'fulfillmentPartnerName': 'fulfillmentPartner',
  'cloudProviderName': 'cloudProvider',

  // ── Round 2 (BA) / Round 3 (Gen) — Financial Terms ──
  'consultationFee': 'consultationFee',
  'telehealthCost': 'telehealthCost',
  'pcRetainer': 'pcRetainer',
  'medicalDirectorRetainer': 'mdRetainer',
  'ipLicenseRoyaltyPercent': 'ipLicenseRoyaltyPercent',
  'supplementMarginPercent': 'supplementMarginPercent',

  // ── Round 2 (BA) / Round 3 (Gen) — Jurisdictions ──
  'operatingJurisdictions': 'jurisdictions',

  // ── Round 3 (BA) / Round 4 (Gen) — Document Variables ──
  'arbitrationCounty': 'arbitrationCounty',
  'tier1Price': 'tier1Price',
  'tier1PriceDescription': 'tier1PriceDescription',
  'lastUpdatedDate': 'lastUpdatedDate',

  // ── Round 3 (BA) / Round 4 (Gen) — Policy URLs ──
  'privacyPolicyUrl': 'privacyPolicyUrl',
  'consumerHealthDataPolicyUrl': 'chdPolicyUrl',
  'hipaaNoticeUrl': 'hipaaNoticeUrl',
  'telehealthConsentUrl': 'telehealthConsentUrl',
  'cookiePolicyUrl': 'cookiePolicyUrl',

  // ── Round 3 (BA) / Round 4 (Gen) — Tier Configuration ──
  'tier1Name': 'tiers.tier1.name',
  'tier1Enabled': 'tiers.tier1.enabled',
  'tier1PricingModel': 'tiers.tier1.pricingModel',
  'tier1PriceAmount': 'tiers.tier1.price',
  'tier1RefundWindow': 'tiers.tier1.refundWindow',
  'tier1RefundPolicy': 'tiers.tier1.refundPolicy',

  'tier2Name': 'tiers.tier2.name',
  'tier2Enabled': 'tiers.tier2.enabled',
  'tier2PricingModel': 'tiers.tier2.pricingModel',
  'tier2PriceAmount': 'tiers.tier2.price',
  'tier2PriceDescription': 'tiers.tier2.priceDescription',
  'tier2RefundWindow': 'tiers.tier2.refundWindow',
  'tier2RefundPolicy': 'tiers.tier2.refundPolicy',

  'tier3Name': 'tiers.tier3.name',
  'tier3Enabled': 'tiers.tier3.enabled',
  'tier3PricingModel': 'tiers.tier3.pricingModel',
  'tier3PriceAmount': 'tiers.tier3.price',
  'tier3PriceDescription': 'tiers.tier3.priceDescription',
  'tier3RefundWindow': 'tiers.tier3.refundWindow',
  'tier3RefundPolicy': 'tiers.tier3.refundPolicy',
};

// ── REVERSE MAP: BrandConfig field path → Variable Registry ID ──

export const REVERSE_ALIAS_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(FIELD_ALIAS_MAP).map(([k, v]) => [v, k])
);

// ── HELPERS ──

/**
 * Get the BrandConfig field path for a given Variable Registry ID.
 * Falls back to the variableId itself if no alias is defined
 * (supports 1:1 fields where the names already match).
 */
export function getBrandConfigPath(variableId: string): string {
  return FIELD_ALIAS_MAP[variableId] ?? variableId;
}

/**
 * Get the Variable Registry ID for a given BrandConfig field path.
 * Falls back to the brandConfigPath itself if no alias is defined
 * (supports 1:1 fields where the names already match).
 */
export function getVariableId(brandConfigPath: string): string {
  return REVERSE_ALIAS_MAP[brandConfigPath] ?? brandConfigPath;
}

/**
 * Resolve a nested BrandConfig path (e.g., 'tiers.tier1.name') to its value.
 * Returns undefined if the path does not exist.
 */
export function resolveNestedPath(obj: Record<string, any>, path: string): any {
  return path.split('.').reduce((current, key) => {
    if (current === undefined || current === null) return undefined;
    return current[key];
  }, obj);
}

/**
 * Set a value at a nested BrandConfig path (e.g., 'tiers.tier1.name').
 * Creates intermediate objects if they do not exist.
 * Mutates the original object and returns it.
 */
export function setNestedPath(
  obj: Record<string, any>,
  path: string,
  value: any
): Record<string, any> {
  const keys = path.split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (current[keys[i]] === undefined || current[keys[i]] === null) {
      current[keys[i]] = {};
    }
    current = current[keys[i]];
  }
  current[keys[keys.length - 1]] = value;
  return obj;
}

/**
 * Given a variable ID, read its current value from a BrandConfig object.
 * Handles both flat and nested paths via the alias map.
 */
export function readVariableValue(
  variableId: string,
  brandConfig: Record<string, any>
): any {
  const path = getBrandConfigPath(variableId);
  if (path.includes('.')) {
    return resolveNestedPath(brandConfig, path);
  }
  return brandConfig[path];
}

/**
 * Given a variable ID and a value, write it to a BrandConfig object.
 * Handles both flat and nested paths via the alias map.
 * Mutates the original object.
 */
export function writeVariableValue(
  variableId: string,
  value: any,
  brandConfig: Record<string, any>
): void {
  const path = getBrandConfigPath(variableId);
  if (path.includes('.')) {
    setNestedPath(brandConfig, path, value);
  } else {
    brandConfig[path] = value;
  }
}
